// Storefront policy pages. Payment gateways (Razorpay, PhonePe, Cashfree...)
// check that a shop shows these before they activate it. Each page uses the
// owner's own wording when set (brand.policy_<key>), else the standard text
// below, filled in from the shop's Branding details.

export const POLICIES = [
  { key: "terms", label: "Terms & Conditions" },
  { key: "refund", label: "Refund & Cancellation" },
  { key: "shipping", label: "Shipping & Delivery" },
  { key: "privacy", label: "Privacy Policy" },
  { key: "contact", label: "Contact Us" },
];

export function shopFacts(brand = {}) {
  const b = brand || {};
  return {
    shop: (b.shop_name || "").trim() || "our shop",
    legal: (b.legal_name || b.shop_name || "").trim() || "the shop owner",
    address: (b.shop_address || "").trim(),
    phone: (b.support_phone || b.whatsapp_number || "").trim(),
    email: (b.support_email || "").trim(),
    gstin: (b.gstin || "").trim(),
    refundDays: Number(b.refund_window_days) || 7,
    processingDays: Number(b.refund_processing_days) || 7,
    delivery: (b.delivery_time || "").trim() || "3-7 working days",
  };
}

function contactLine(f) {
  return [f.phone && `phone/WhatsApp ${f.phone}`, f.email && `email ${f.email}`].filter(Boolean).join(" or ")
    || "the contact details on our Contact Us page";
}

// Standard text as a list of [heading, paragraph] pairs.
export function defaultPolicy(key, brand) {
  const f = shopFacts(brand);
  const contact = contactLine(f);
  switch (key) {
    case "terms":
      return [
        ["About us", `This online shop is run by ${f.legal}${f.address ? `, ${f.address}` : ""}. By placing an order you agree to these terms.`],
        ["Products and prices", "We describe products and show prices as accurately as we can. Prices are in Indian Rupees and include applicable taxes unless stated otherwise. Product photos may differ slightly from the actual item. If a price or description is wrong, we will contact you before processing the order and you may cancel it."],
        ["Orders", "An order is confirmed when we accept it and, for online payment, when the payment succeeds. We may decline or cancel an order if the product is out of stock, the delivery address is not serviceable, or we suspect misuse. If we cancel a paid order, the full amount is refunded."],
        ["Payment", "Online payments are processed securely by our payment partner; we do not see or store your card or UPI PIN details. Cash on delivery is available only where shown at checkout."],
        ["Warranty", "Products carry the manufacturer's warranty where applicable. Warranty service is provided by the manufacturer or its authorised service centres, as per their terms; we will help you with the process."],
        ["Software and downloads", "Software and files offered for download are for your own use under their licence terms. Do not redistribute them unless the licence allows it."],
        ["Liability", "We are not liable for indirect losses arising from the use of products bought here. Our liability for any order is limited to the amount paid for it."],
        ["Governing law", `These terms are governed by the laws of India. Questions about them: ${contact}.`],
      ];
    case "refund":
      return [
        ["Cancellation", "You can cancel an order before it is dispatched, from your Orders page or by contacting us. Paid orders cancelled before dispatch are refunded in full."],
        ["Returns", `If a product arrives damaged, defective or different from what you ordered, tell us within ${f.refundDays} days of delivery, with photos if possible. We will arrange a replacement, repair or refund after checking the item. The product must be unused, with its original box, accessories and invoice.`],
        ["Not returnable", "Software and downloadable products once delivered or activated, consumables that have been opened (such as ink, toner and cables), and items damaged through misuse cannot be returned, except where the law requires."],
        ["Refunds", `Approved refunds are sent to the original payment method within ${f.processingDays} working days. Your bank may take a few more days to show the credit. Cash on delivery orders are refunded by bank transfer or UPI to an account you give us.`],
        ["How to ask", `Contact us on ${contact} with your order number.`],
      ];
    case "shipping":
      return [
        ["Where we deliver", "We deliver to the areas and PIN codes accepted at checkout. If your PIN code isn't accepted, contact us and we'll try to help."],
        ["Delivery time", `Orders are usually delivered within ${f.delivery} after confirmation. Delays can happen because of holidays, weather or courier issues; we'll keep you informed.`],
        ["Charges", "Any delivery charge is shown at checkout before you pay."],
        ["Tracking", "You can follow your order from the Orders page or the tracking link we send you."],
        ["Receiving your order", "Please check the package at delivery. If it is damaged or tampered with, note it with the delivery person and tell us within 48 hours."],
        ["Questions", `Contact us on ${contact}.`],
      ];
    case "privacy":
      return [
        ["What we collect", "Your name, phone number, email, delivery address and order details, and the messages you send us. Payment details are handled by our payment partner and are not stored by us."],
        ["Why", "To process and deliver your orders, contact you about them, provide support, and keep records required by law."],
        ["Sharing", "We share only what is needed with delivery partners, payment partners and service providers who help us run the shop. We do not sell your personal information."],
        ["Security and storage", "Your information is stored securely and kept only as long as needed for these purposes or as the law requires."],
        ["Your choices", `You can ask to see, correct or delete your information, or stop messages from us, by contacting us on ${contact}.`],
        ["Changes", "We may update this policy; the current version is always on this page."],
      ];
    default:
      return [];
  }
}
